import * as React from 'react';
import axios, {AxiosResponse, AxiosError} from 'axios';
import queryString from 'query-string';
import {ID, ErrorObject, DeleteObject, PreviewObject} from 'types';
import AppStore, {Theme} from 'stores/AppStore';

export enum FileType {
  Image = 'image',
  Video = 'video',
  Audio = 'audio',
  Document = 'document',
  External = 'external'
}

export interface FileObject {
  id: ID;
  type: FileType;
  title: string;
  uri: string;
  url: string;
  meta?: {
    mimetype: string;
    size: number;
  };
  userID?: ID;
}

export interface GetFilesParams {
  title?: string;
  skip?: number;
  limit?: number;
  my?: string;
}

type UseLibraryReturn<T extends FileObject | FileObject[]> = {
  status: {
    isLoaded: boolean,
    setLoaded: React.Dispatch<React.SetStateAction<boolean>>
  },
  data: {
    files: T | undefined,
    setFiles: React.Dispatch<React.SetStateAction<T | undefined>>,
  }
};

/**
 * The hook stores fetch status and files data
 * @return {UseLibraryReturn}
 */
export function useLibrary
<T extends FileObject | FileObject[]>(): UseLibraryReturn<T> {
  const [
    isLoaded,
    setLoaded,
  ] = React.useState<boolean>(false);
  const [
    files,
    setFiles,
  ] = React.useState<T>();
  return {
    status: {
      isLoaded,
      setLoaded,
    },
    data: {
      files,
      setFiles,
    },
  };
}

/**
 * Get file path
 * @return {string}
 * @param {FileObject} id
 */
export function getFilePath(id: FileObject['id']): string {
  return `${process.env.REACT_APP_API_BASE_URL}/files/${id}`;
}

/**
 * Get an image from the object preview or return a not found image.
 * @param {PreviewObject | null} preview
 * @return {string}
 */
export function getImageByPreview(preview?: PreviewObject | null): string {
  return preview ? (
    getFilePath(preview.id)
  ) : (
    AppStore.theme === Theme.Dark ? (
      `${process.env.PUBLIC_URL}/images/not-found.png`
    ) : `${process.env.PUBLIC_URL}/images/not-found-light.png`
  );
}

/**
 * Get file object by id
 * @param {FileObject} id
 * @return {Promise<FileObject | ErrorObject>}
 */
export async function getFileByID(id: FileObject['id']) {
  return axios({
    method: 'GET',
    url: `${process.env.REACT_APP_API_BASE_URL}/files/info/${id}`,
  }).then((response: AxiosResponse<FileObject>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Get all files
 * @param {GetFilesParams} params
 * @return {Promise<AxiosResponse<FileObject[]> | ErrorObject>}
 */
export async function getFiles(params?: GetFilesParams) {
  return axios({
    method: 'GET',
    url: queryString.stringifyUrl({
      url: `${process.env.REACT_APP_API_BASE_URL}/files`,
      query: params && {
        ...params,
      },
    }),
  }).then((response: AxiosResponse<FileObject[]>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

export type UploadFileType = File | string;

export const imageTypes = [
  'image/jpg',
  'image/jpeg',
  'image/png',
  'image/gif',
];

/**
 * Is file as image type
 * @param {File} file
 * @return {boolean}
 */
function isImage(file: File): boolean {
  return imageTypes.find((type) => (
    file.type === type
  )) !== undefined;
}

export const videoTypes = [
  'video/quicktime',
  'audio/mp4',
  'audio/mpeg',
  'audio/vnd.wave',
];

/**
 * Is file as video type
 * @param {File} file
 * @return {boolean}
 */
function isVideo(file: File): boolean {
  return videoTypes.find((type) => (
    file.type === type
  )) !== undefined;
}

export const audioTypes = [];

/**
 * Is file as audio type
 * @param {File} file
 * @return {boolean}
 */
function isAudio(file: File): boolean {
  return audioTypes.find((type) => (
    file.type === type
  )) !== undefined;
}

export const documentTypes = [
  'application/pdf',
  'application/msword',
  'application/ms-doc',
  'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

/**
 * Is file as document type
 * @param {File} file
 * @return {boolean}
 */
function isDocument(file: File): boolean {
  return documentTypes.find((type) => (
    file.type === type
  )) !== undefined;
}

/**
 * Upload file
 * @param {UploadFileType} file
 * @return {Promise<void>}
 */
export async function uploadFile(file: UploadFileType) {
  const formData = new FormData();

  if (typeof file === 'string') {
    formData.append('link', file);
    formData.append('type', 'external');
  } else {
    formData.append('file', file);
    isImage(file) && formData.append('type', 'image');
    isVideo(file) && formData.append('type', 'video');
    isAudio(file) && formData.append('type', 'audio');
    isDocument(file) && formData.append('type', 'document');
  }

  return axios({
    method: 'POST',
    url: `${process.env.REACT_APP_API_BASE_URL}/files/upload`,
    data: formData,
  }).then((response: AxiosResponse<FileObject>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Upload link
 * @param {string} link
 * @return {Promise<void>}
 */
export async function uploadLink(link: string) {
  return axios({
    method: 'POST',
    url: `${process.env.REACT_APP_API_BASE_URL}/files/external`,
    data: JSON.stringify({
      link,
    }),
  }).then((response: AxiosResponse<FileObject>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Delete the file
 * @param {FileObject} id
 * @return {Promise<DeleteObject | ErrorObject>}
 */
export async function deleteFile(id: FileObject['id']) {
  return axios({
    method: 'DELETE',
    url: `${process.env.REACT_APP_API_BASE_URL}/files/${id}`,
  }).then((response: AxiosResponse<DeleteObject>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}
